<script setup lang="ts">
// Global notification bell - lives in the left nav's bottom utility
// group (above Help & docs) so the unread badge is present on every
// surface. `variant="nav"` renders the trigger as a full-width nav row
// (icon + label + unread pill, rail-aware via `collapsed`); the default
// 'chrome' variant keeps the bare 32px icon button for ad-hoc
// placements. The dropdown mirrors AppNav's account-menu pattern
// exactly: a trigger that computes its position from
// getBoundingClientRect(), a body-teleported overlay, capture-phase
// outside-click + Escape to close.
import AppIcon from '@/components/AppIcon.vue';
import { onMounted, onBeforeUnmount, nextTick, ref } from 'vue';
import { RouterLink } from 'vue-router';
import EmptyState from '@/components/EmptyState.vue';
import NotificationItem from '@/components/NotificationItem.vue';
import { useNotificationsStore } from '@/stores/notifications';
import { useI18n } from '@/lib/i18n';
import { Tooltip } from '@hq/chat-ui';

const { t } = useI18n();

withDefaults(defineProps<{
  variant?: 'chrome' | 'nav';
  collapsed?: boolean;
}>(), { variant: 'chrome', collapsed: false });

const notifications = useNotificationsStore();

const menuOpen = ref(false);
const triggerEl = ref<HTMLElement | null>(null);
const menuEl = ref<HTMLElement | null>(null);
const menuStyle = ref<Record<string, string>>({});

function openMenu(): void {
  const el = triggerEl.value;
  if (!el) return;
  const r = el.getBoundingClientRect();
  // Anchor to whichever screen corner the bell lives nearest: from the
  // top-right chrome the panel drops DOWN and right-aligns to the bell;
  // from a bottom-left cluster it pops UP and left-aligns. A 12px
  // viewport gutter keeps the panel from gluing to an edge either way.
  const style: Record<string, string> = {};
  if (r.top < window.innerHeight / 2) {
    style.top = `${Math.round(r.bottom + 8)}px`;
  } else {
    style.bottom = `${Math.round(window.innerHeight - r.top + 8)}px`;
  }
  if (r.left + r.right > window.innerWidth) {
    style.right = `${Math.max(Math.round(window.innerWidth - r.right), 12)}px`;
  } else {
    style.left = `${Math.max(Math.round(r.left), 12)}px`;
  }
  menuStyle.value = style;
  menuOpen.value = true;
  notifications.openPanel();
  // The opening click already passed mousedown; attaching now won't
  // self-close. Capture phase catches clicks in any stacking context.
  document.addEventListener('mousedown', onDocPointer, true);
  document.addEventListener('keydown', onDocKey);
  // Move focus into the panel so keyboard users land in it (and Escape, handled
  // on the panel, works immediately); closeMenu returns focus to the trigger.
  void nextTick(() => menuEl.value?.focus());
}

function closeMenu(): void {
  if (!menuOpen.value) return;
  menuOpen.value = false;
  notifications.closePanel();
  document.removeEventListener('mousedown', onDocPointer, true);
  document.removeEventListener('keydown', onDocKey);
}

function toggleMenu(): void {
  if (menuOpen.value) closeMenu();
  else openMenu();
}

function onDocPointer(e: MouseEvent): void {
  const t = e.target as Node;
  if (menuEl.value?.contains(t) || triggerEl.value?.contains(t)) return;
  closeMenu();
}

function onDocKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    e.preventDefault();
    closeMenu();
    void nextTick(() => triggerEl.value?.focus());
  }
}

function onViewAll(): void { closeMenu(); }

function onMarkAllRead(): void { void notifications.markAllRead(); }

onMounted(() => {
  void notifications.fetch();
  notifications.startPolling();
});

onBeforeUnmount(() => {
  notifications.stopPolling();
  document.removeEventListener('mousedown', onDocPointer, true);
  document.removeEventListener('keydown', onDocKey);
});
</script>

<template>
  <Tooltip :label="variant === 'nav' && !collapsed ? undefined : t('notifications.title')">
    <button
      ref="triggerEl"
      type="button"
      class="notif-bell"
      :class="{
        'notif-bell--open': menuOpen,
        'notif-bell--nav': variant === 'nav',
        'notif-bell--rail': variant === 'nav' && collapsed,
      }"
      aria-haspopup="dialog"
      :aria-expanded="menuOpen"
      :aria-label="t('notifications.title')"
      @click="toggleMenu"
    >
      <AppIcon name="bell" :size="18" />
      <span v-if="variant === 'nav'" class="notif-bell__label">{{ t('notifications.title') }}</span>
      <span
        v-if="notifications.unread > 0"
        class="notif-bell__badge"
        aria-hidden="true"
      >{{ notifications.unread > 99 ? '99+' : notifications.unread }}</span>
    </button>
  </Tooltip>

  <!-- Teleported to <body> so no ancestor overflow clips it; positioned
       from the bell via menuStyle (down + right-aligned in the header
       chrome, up + left-aligned from a bottom cluster). Outside-click +
       Escape close it. -->
  <Teleport to="body">
    <Transition name="notif-menu">
      <div
        v-if="menuOpen"
        ref="menuEl"
        class="notif-menu"
        :style="menuStyle"
        role="dialog"
        tabindex="-1"
        :aria-label="t('notifications.title')"
      >
        <div class="notif-menu__head">
          <span class="notif-menu__title">{{ t('notifications.title') }}</span>
          <button
            type="button"
            class="notif-menu__markall"
            :disabled="notifications.unread === 0"
            @click="onMarkAllRead"
          >
            <AppIcon name="checks" :size="14" />
            <span>{{ t('notifications.mark_all_read') }}</span>
          </button>
        </div>

        <div class="notif-menu__list">
          <NotificationItem
            v-for="n in notifications.items"
            :key="n.id"
            :notification="n"
          />
          <EmptyState
            v-if="!notifications.items.length"
            :icon="'bell'"
            :title="t('notifications.empty_title')"
            :body="t('notifications.empty_body_bell')"
          />
        </div>

        <RouterLink
          :to="{ name: 'notifications' }"
          class="notif-menu__footer"
          @click="onViewAll"
        >{{ t('notifications.view_all') }}</RouterLink>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
@layer layout {
  .notif-bell {
    position: relative;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: inline-grid;
    place-items: center;
    background: transparent;
    color: var(--nav-ink);
    border: 0;
    border-radius: var(--radius-input);
    cursor: pointer;
    transition:
      background var(--dur-1) var(--ease-out),
      color var(--dur-1) var(--ease-out);

    &:hover,
    &--open {
      background: var(--nav-item-hover);
      color: var(--nav-ink-strong);
    }
    &:focus-visible {
      outline: 2px solid var(--border-focus);
      outline-offset: 2px;
    }
  }
  // Reuse the AppNav nav__badge look (mono, 10px, 700, accent fill), but
  // overlay it on the bell's top-right corner.
  .notif-bell__badge {
    position: absolute;
    top: -3px;
    right: -3px;
    font-family: var(--font-mono);
    font-size: 10px;
    font-weight: 700;
    line-height: 1.4;
    padding: 0 4px;
    border-radius: var(--radius-chip);
    background: var(--accent-default);
    color: var(--accent-text);
    min-width: 1.5em;
    text-align: center;
    letter-spacing: var(--tracking-wide);
  }

  // Nav-row variant: the bell as a full-width left-menu row, matching
  // AppNav's .nav__link look (icon + label, unread count as an inline
  // pill on the right instead of the corner overlay).
  .notif-bell--nav {
    width: 100%;
    height: auto;
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: 6px var(--space-3);  // mirrors AppNav's .nav__link row padding
    border-radius: var(--radius-button);
    font: inherit;
    font-size: var(--text-sm);
    font-weight: 500;
    text-align: left;

    .notif-bell__label { flex: 1; }
    .notif-bell__badge {
      position: static;
      padding: 2px 6px;
    }
  }

  // Rail (collapsed sidebar): icon-only centered square, same box as
  // AppNav's rail links; label + badge collapse like nav__badge does.
  .notif-bell--rail {
    width: 36px;
    height: 36px;
    justify-content: center;
    margin-inline: auto;
    padding: 0;
    gap: 0;

    .notif-bell__label,
    .notif-bell__badge { display: none; }
  }
}
</style>

<!-- Unscoped: the dropdown is teleported to <body>, so scoped class hashes
     don't reach it. Selectors are prefixed to avoid collisions. -->
<style lang="scss">
.notif-menu {
  position: fixed;
  z-index: 9000;
  width: 360px;
  max-width: calc(100vw - 24px);
  display: flex;
  flex-direction: column;
  background: var(--surface-panel);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-overlay);
  overflow: hidden;
}
/* The panel is focused programmatically on open (tabindex=-1); no focus ring. */
.notif-menu:focus { outline: none; }
.notif-menu__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-3) var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
}
.notif-menu__title {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--ink-primary);
}
.notif-menu__markall {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border: 0;
  border-radius: var(--radius-input);
  background: transparent;
  color: var(--ink-muted);
  font: inherit;
  font-size: var(--text-xs);
  font-weight: 500;
  cursor: pointer;
  transition:
    background var(--dur-1) var(--ease-out),
    color var(--dur-1) var(--ease-out);

  &:hover:not(:disabled) { background: var(--surface-sunken); color: var(--ink-primary); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &:focus-visible {
    outline: 2px solid var(--border-focus);
    outline-offset: -2px;
  }
}
.notif-menu__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 360px;
  overflow-y: auto;
  padding: var(--space-2);
}
.notif-menu__footer {
  display: block;
  padding: var(--space-3);
  text-align: center;
  border-top: 1px solid var(--border-subtle);
  color: var(--ink-muted);
  font-size: var(--text-sm);
  font-weight: 500;
  text-decoration: none;
  transition: background var(--dur-1) var(--ease-out), color var(--dur-1) var(--ease-out);

  &:hover { background: var(--surface-sunken); color: var(--ink-primary); }
  &:focus-visible {
    outline: 2px solid var(--border-focus);
    outline-offset: -2px;
  }
}

.notif-menu-enter-active,
.notif-menu-leave-active {
  transition:
    opacity var(--dur-1) var(--ease-out),
    transform var(--dur-1) var(--ease-out);
}
.notif-menu-enter-from,
.notif-menu-leave-to {
  opacity: 0;
  transform: translateY(4px) scale(0.98);
  transform-origin: bottom left;
}
</style>
